import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { getStrapiMedia, getStrapiMediaAttributes } from '../../lib/strapi';
import { getContentFontStyle, getHeadingFontStyle } from '../../lib/contentBlockFontSize';
import { useGlobal } from '../../context/GlobalContext';
import { getIconFallbackLabel } from '../../lib/uiText';
import styles from './ContactForm.module.css';

const EMBEDDED_FORM_URL = 'https://cloud.info.origina.mx/WFSF26K';

const CONTACT_FORM_HEADING_FONT_SIZE_FALLBACKS = {
  h1: '2em',
  h2: '1.5em',
  h3: '1.1em',
};
const CONTACT_FORM_PARAGRAPH_FONT_SIZE = '0.95em';
const CONTACT_FORM_LIST_FONT_SIZE = '0.95em';

const hasRenderableContentBlock = (block) => {
  if (!block) return false;

  switch (block.__component) {
    case 'shared.heading':
    case 'shared.paragraph':
      return Boolean(String(block.text || '').trim());
    case 'shared.list':
      return Array.isArray(block.items) && block.items.some((item) => String(item?.text || '').trim());
    default:
      return false;
  }
};

const ContactForm = ({ data }) => {
  const { locale } = useGlobal();
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  const backgroundImage = data?.backgroundImage?.url
    ? getStrapiMedia(data.backgroundImage.url)
    : null;
  const hasHero = Boolean(backgroundImage || data?.overlayTitle || data?.overlaySubtitle);

  /* ─── Animation ─── */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const animItems = card.querySelectorAll(`.${styles.contactForm__animateItem}`);
    gsap.set(card, { opacity: 0, y: 40 });
    gsap.set(animItems, { opacity: 0, y: 20 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || hasAnimatedRef.current) return;
          hasAnimatedRef.current = true;
          const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
          tl.to(card, { opacity: 1, y: 0, duration: 0.6 });
          if (animItems.length) {
            tl.to(animItems, { opacity: 1, y: 0, duration: 0.5, stagger: 0.06 }, '-=0.25');
          }
          observer.disconnect();
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(sectionRef.current || card);
    return () => observer.disconnect();
  }, []);

  if (!data) return null;


  /* ─── Content blocks (left side) ─── */
  const renderContentBlock = (block, index) => {
    if (!block) return null;

    switch (block.__component) {
      case 'shared.heading': {
        const level = block.level || 'h2';
        const Tag = level;
        const headingStyle = getHeadingFontStyle(
          block?.fontSize,
          level,
          CONTACT_FORM_HEADING_FONT_SIZE_FALLBACKS
        );
        const cls = [
          styles.contactForm__heading,
          styles[`contactForm__heading--${level}`],
          styles.contactForm__animateItem,
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <Tag key={`h-${index}`} className={cls} style={headingStyle}>
            {block.text}
          </Tag>
        );
      }
      case 'shared.paragraph': {
        const paragraphStyle = getContentFontStyle(block?.fontSize, CONTACT_FORM_PARAGRAPH_FONT_SIZE);
        return (
          <p
            key={`p-${index}`}
            className={`${styles.contactForm__text} ${styles.contactForm__animateItem}`}
            style={paragraphStyle}
          >
            {block.text}
          </p>
        );
      }
      case 'shared.list': {
        const listItemStyle = getContentFontStyle(block?.fontSize, CONTACT_FORM_LIST_FONT_SIZE);
        const hasIcon = block.icon?.url;
        const iconMedia = hasIcon
          ? getStrapiMediaAttributes(block.icon, {
            altFallback: getIconFallbackLabel(locale, 'list'),
            titleFallback: getIconFallbackLabel(locale, 'list'),
          })
          : null;
        const ListTag = !hasIcon && block.ordered ? 'ol' : 'ul';
        const listCls = [
          styles.contactForm__list,
          hasIcon
            ? styles['contactForm__list--icons']
            : block.ordered
              ? styles['contactForm__list--ordered']
              : styles['contactForm__list--unordered'],
          styles.contactForm__animateItem,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <ListTag key={`list-${index}`} className={listCls}>
            {block.items?.map((item, i) => (
              <li key={`li-${i}`} className={styles.contactForm__listItem} style={listItemStyle}>
                {iconMedia?.src && (
                  <img
                    className={styles.contactForm__listIcon}
                    src={iconMedia.src}
                    alt={iconMedia.alt}
                    title={iconMedia.title}
                    aria-hidden="true"
                  />
                )}
                <span>{item.text}</span>
              </li>
            ))}
          </ListTag>
        );
      }
      default:
        return null;
    }
  };

  const contentBlocks = (data?.content || []).filter(hasRenderableContentBlock);
  const hasContent = contentBlocks.length > 0;

  const rootClassName = [
    styles.contactForm,
    hasHero ? styles['contactForm--hasHero'] : styles['contactForm--noHero'],
    !hasContent ? styles['contactForm--noContent'] : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section ref={sectionRef} className={rootClassName}>
      {/* Hero background header */}
      {hasHero && (
        <div
          className={styles.contactForm__hero}
          style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
        >
          {backgroundImage && <div className={styles.contactForm__heroOverlay} />}
          {data.overlayTitle && (
            <p className={`${styles.contactForm__heroTitle} ${styles.contactForm__animateItem}`}>
              {data.overlayTitle}
            </p>
          )}
          {data.overlaySubtitle && (
            <h2 className={`${styles.contactForm__heroSubtitle} ${styles.contactForm__animateItem}`}>
              {data.overlaySubtitle}
            </h2>
          )}
        </div>
      )}

      {/* Card with content + form */}
      <div ref={cardRef} className={styles.contactForm__card}>
        {/* Left column: descriptive content */}
        {hasContent && (
          <div className={styles.contactForm__content}>
            {contentBlocks.map((block, idx) => renderContentBlock(block, idx))}
          </div>
        )}

        {/* Right column: embedded form */}
        <div className={styles.contactForm__formWrapper}>
          <div className={`${styles.contactForm__iframeShell} ${styles.contactForm__animateItem}`}>
            <iframe
              className={styles.contactForm__iframe}
              src={EMBEDDED_FORM_URL}
              title={data?.overlayTitle || data?.overlaySubtitle || 'Formulario de contacto'}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="clipboard-write"
            />
          </div>
        </div>
      </div>

    </section>
  );
};

export default ContactForm;
